// =============================================================================
// Add books (§5.6, §9.1).
//
// Optimised for BULK CATALOGUING, not for adding the occasional book. The
// school has a full collection on shelves and no system, so the first real job
// is entering several hundred books in a sitting. That makes keystrokes per
// book the thing that matters, not form elegance.
//
// Consequences of that:
//   - the form never leaves the screen; it clears and refocuses after each save
//   - a running list of what was just added stays visible, newest first, with
//     the generated copy codes — that list IS the print queue
//   - duplicate titles are detected BEFORE saving and offer "add copies to the
//     existing title" instead, which is the correct action and one tap
//
// Exported twice on purpose: as a panel inside the Books screen, and as a
// standalone route so a direct link still works on a phone (§2.2).
// =============================================================================

import { el, mount } from '../lib/dom.js';
import { T } from '../lib/strings.js';
import { addTitleWithCopies, addCopiesToTitle, findTitlesByName, listCategories, listBranches } from '../lib/api.js';
import { getStaff } from '../lib/auth.js';

export function addBookPanel({ onAdded } = {}) {
  let session = [];
  let categories = [];
  let branches = [];
  let busy = false;
  let duplicate = null;

  // Copies belong to a branch, and an admin has no home branch (§4) — so an
  // admin must say which branch the books are going to. Front desk staff have
  // exactly one, so they are never asked.
  const needsBranch = () => getStaff()?.role === 'admin';

  const view = el('div', { class: 'add-book' });

  const fields = {
    title: el('input', { type: 'text', class: 'text-input', required: true,
      'aria-label': T.bookTitle, placeholder: T.bookTitle, enterkeyhint: 'next', autocomplete: 'off' }),
    author: el('input', { type: 'text', class: 'text-input',
      'aria-label': T.author, placeholder: T.authorOptional, enterkeyhint: 'next', autocomplete: 'off' }),
    category: el('select', { class: 'text-input', 'aria-label': T.category }),
    // Optional, and last in the form: a desk cataloguing a stack of books
    // should not have to tab past two fields it usually leaves blank.
    publishedYear: el('input', { type: 'number', class: 'text-input',
      min: '1400', max: '2100', step: '1', inputmode: 'numeric',
      'aria-label': T.publishedYear, placeholder: T.publishedYearOptional,
      enterkeyhint: 'next', autocomplete: 'off' }),
    publishedPlace: el('input', { type: 'text', class: 'text-input',
      'aria-label': T.publishedPlace, placeholder: T.publishedPlaceOptional,
      enterkeyhint: 'next', autocomplete: 'off' }),
    copies: el('input', { type: 'number', class: 'text-input', min: '1', max: '50', value: '1',
      'aria-label': T.copies, enterkeyhint: 'done', inputmode: 'numeric' }),
    branch: el('select', { class: 'text-input', 'aria-label': T.branch }),
  };

  async function loadLists() {
    try { categories = await listCategories(); } catch { categories = []; }
    mount(fields.category,
      el('option', { value: '' }, T.categoryNone),
      ...categories.map((c) => el('option', { value: c.name }, c.name)));

    if (needsBranch()) {
      try { branches = await listBranches(); } catch { branches = []; }
      mount(fields.branch, ...branches.map((b) => el('option', { value: b.id }, b.name)));
    }
  }

  const chosenBranch = () => (needsBranch() ? fields.branch.value || null : null);

  function render() {
    const saveBtn = el('button', {
      class: 'btn btn-primary', type: 'submit', disabled: busy,
      // "Save book", not "Add book": the Books screen already has an "Add book"
      // button that opens this panel, and two identical labels on one screen is
      // a coin flip for whoever is using it.
    }, busy ? T.saving : T.saveBook);

    // Each label paired with its control inside a .field, and the button in a
    // .form-actions row — the same two wrappers every other form on the app
    // uses. Bare labels and a full-width button made this panel the odd one out.
    const field = (label, control, hint) =>
      el('div', { class: 'field' },
        el('label', { class: 'field-label' }, label), control,
        hint ? el('p', { class: 'muted' }, hint) : null);

    const form = el('form', { class: 'card', onSubmit: submit },
      el('h2', { class: 'card-title' }, T.newBook),
      field(T.bookTitle, fields.title),
      field(T.author, fields.author),
      field(T.category, fields.category),
      field(T.publishedYear, fields.publishedYear),
      field(T.publishedPlace, fields.publishedPlace),
      field(T.copies, fields.copies, T.copiesHint),
      needsBranch() ? field(T.branch, fields.branch) : null,
      el('div', { class: 'form-actions' }, saveBtn),
    );

    mount(view,
      duplicate ? renderDuplicate() : form,
      session.length ? renderSession() : null,
    );

    if (!duplicate) queueMicrotask(() => fields.title.focus());
  }

  // §9.1: catching a duplicate before it is saved is the single highest-value
  // check in bulk entry. Two "Cat in the Hat" titles with one copy each is a
  // mess to unpick later; one title with two copies is correct.
  function renderDuplicate() {
    return el('section', { class: 'card card-warn' },
      el('h2', { class: 'card-title' }, T.possibleDuplicate),
      el('p', {}, T.duplicateExplain(duplicate.title)),
      ...duplicate.existing.map((t) => el('div', { class: 'loan-row' },
        el('span', { class: 'loan-title' }, t.title),
        el('span', { class: 'muted' }, t.author || ''),
        el('button', { class: 'btn btn-primary', onClick: () => confirmAddCopies(t) },
          T.addCopiesToThis(duplicate.copies)),
      )),
      el('div', { class: 'form-actions' },
        el('button', { class: 'btn', onClick: () => confirmNewTitle() }, T.createSeparateTitle),
        el('button', { class: 'btn', onClick: () => { duplicate = null; render(); } }, T.cancel),
      ),
    );
  }

  function renderSession() {
    return el('section', { class: 'card' },
      el('div', { class: 'session-head' },
        el('h2', { class: 'card-title' }, T.addedThisSession(session.length)),
        el('button', { class: 'btn btn-sm', onClick: printLabels }, T.printLabels),
      ),
      ...session.map((b) => el('div', { class: 'basket-row' },
        el('span', { class: 'basket-title' }, b.title),
        el('span', { class: 'basket-code' }, b.copy_codes.join(', ')),
      )),
    );
  }

  function readForm() {
    return {
      title: fields.title.value.trim(),
      author: fields.author.value.trim() || null,
      category: fields.category.value || null,
      publishedYear: parseYear(fields.publishedYear.value),
      publishedPlace: fields.publishedPlace.value.trim() || null,
      copies: Math.max(1, Math.min(50, parseInt(fields.copies.value, 10) || 1)),
      branchId: chosenBranch(),
    };
  }

  // A year outside a plausible range is almost always a typo — 20I8, or a
  // page count typed into the wrong box. The database rejects it too (0011);
  // catching it here means the message arrives before the book is saved.
  function parseYear(raw) {
    const v = String(raw || '').trim();
    if (!v) return null;
    const n = parseInt(v, 10);
    if (!Number.isFinite(n) || n < 1400 || n > 2100) return null;
    return n;
  }

  function flash(message, kind = 'error') {
    const note = el('p', { class: kind === 'error' ? 'error' : 'notice', role: 'alert' }, message);
    view.prepend(note);
    setTimeout(() => note.remove(), 5000);
  }

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    const data = readForm();
    if (!data.title) { fields.title.focus(); return; }

    busy = true; render();
    try {
      const existing = await findTitlesByName(data.title);
      if (existing.length) {
        duplicate = { ...data, existing };
        busy = false; render();
        return;
      }
      await confirmNewTitle(data);
    } catch (err) {
      busy = false; render();
      flash(err.message);
    }
  }

  async function confirmNewTitle(data = duplicate) {
    busy = true; duplicate = null; render();
    try {
      const result = await addTitleWithCopies(data);
      session.unshift({ title: data.title, copy_codes: result.copy_codes ?? [] });
      resetForm(); busy = false; render();
      flash(T.addedWithCodes(data.title, result.copy_codes?.length ?? 0), 'notice');
      onAdded?.();
    } catch (err) {
      busy = false; render();
      flash(err.message);
    }
  }

  async function confirmAddCopies(title) {
    const copies = duplicate.copies;
    busy = true; duplicate = null; render();
    try {
      const result = await addCopiesToTitle(title.id, copies, chosenBranch());
      session.unshift({ title: title.title, copy_codes: result.copy_codes ?? [] });
      resetForm(); busy = false; render();
      flash(T.addedWithCodes(title.title, result.copy_codes?.length ?? 0), 'notice');
      onAdded?.();
    } catch (err) {
      busy = false; render();
      flash(err.message);
    }
  }

  function resetForm() {
    fields.title.value = '';
    fields.author.value = '';
    // Category and copy count are deliberately NOT cleared: a cataloguing run
    // works through one shelf at a time, so the next book is usually the same
    // category, and re-picking it every time is the most tedious keystroke.
    queueMicrotask(() => fields.title.focus());
  }

  // Print-ready QR label sheet for everything added this session.
  // Plain A4, matte tape over the top — see §9.2.
  async function printLabels() {
    const codes = session.flatMap((b) => b.copy_codes.map((c) => ({ code: c, title: b.title })));
    if (!codes.length) return;
    const { openLabelSheet } = await import('../lib/labels.js');
    openLabelSheet(codes);
  }

  loadLists().then(render);
  return view;
}

// Standalone route (#/add). Absent from desktop navigation now that Books owns
// adding, but kept reachable — a direct link must always resolve (§2.2).
export function addBooksScreen(root) {
  const view = el('div', { class: 'screen' },
    el('h1', { class: 'screen-title' }, T.addBooksTitle),
    addBookPanel({}),
  );
  mount(root, view);
  return { destroy: () => {} };
}
